import { CampaignStep } from 'modules/campaigns/editor/types';
import { CampaignState } from 'modules/domains/campaign';
import { Campaign, CampaignStateDefinitions } from 'modules/campaignIndex/types/Campaign';
import { WorkflowType } from 'modules/workflows/enums';
import { campaignStates } from 'utils/campaigns';
import { TemplateLocales } from 'modules/templateIndex/templateFolders/types';

/**
 * CAMPAIGNS
 */
export const campaignAnalytics = (id?: number) => `/analytics/campaignPerformance?campaignId=${id}`;
export const reviewCampaign = (id?: number) => `/campaigns/review?campaignId=${id}`;
export const getCampaignRoute = (id?: number, campaignState?: string) =>
  campaignState === campaignStates.FINISHED.value ? campaignAnalytics(id) : reviewCampaign(id);

export const getCampaignEditorUrl = (
  selectedCampaign: Campaign,
  featureFlags?: {
    isCampaignReactificationFeatureOn?: boolean;
    isCampaignAnalyticsReactificationFeatureOn?: boolean;
  },
): string => {
  const {
    isCampaignReactificationFeatureOn = true,
    isCampaignAnalyticsReactificationFeatureOn = true,
  } = featureFlags || {};
  // business logic for workflow triggered campaigns: https://iterable.atlassian.net/browse/APP-3855?focusedCommentId=94153
  const { id, campaignState } = selectedCampaign;
  if (campaignState === CampaignStateDefinitions.Draft) {
    return `/campaigns/${id}?view=Edit`;
  } else if (
    campaignState === CampaignStateDefinitions.Scheduled ||
    campaignState === CampaignStateDefinitions.Ready
  ) {
    return `/campaigns/${id}?view=Summary`;
  } else {
    // (Aborted, Archived, Finished, Recurring, Running) *GroupRecurringCampaigns are handled separately
    if (isCampaignReactificationFeatureOn && isCampaignAnalyticsReactificationFeatureOn) {
      return `/campaigns/${id}?view=Summary`;
    }
    return `/analytics/campaignPerformance?campaignId=${id}`;
  }
};

/**
 * EXPERIMENTATION
 */
export const getExperimentMonitorLink = (experimentId: number) =>
  `/experiments/monitor?experimentId=${experimentId}`;
export const getVariationTemplateLink = (variationId: number) =>
  `/templates/editor?templateId=${variationId}`;
export const getExperimentsIndexPageLink = () => `/experiments`;
export const getCampaignEditorLink = (campaignId: number, campaignState: CampaignState) => {
  const view = campaignState === CampaignState.Draft ? 'Edit' : 'Summary';
  return `/campaigns/${campaignId}?view=${view}`;
};
export const getCampaignEditorExperimentsSectionLink = (
  campaignId: number,
  campaignState: CampaignState,
) => {
  const view =
    campaignState === CampaignState.Running
      ? '?view=Summary'
      : `?view=Edit&step=${CampaignStep.ConversionAndExperiment}`;
  return `/campaigns/${campaignId}${view}&isExperimentModalOpen=true`;
};
export const getCreateExperimentLink = (campaignId: number) =>
  getCampaignEditorExperimentsSectionLink(campaignId, CampaignState.Draft);

export const getCampaignEditorLinkWithExperimentTakeover = (
  campaignId: number,
  experimentId: number,
) => `/campaigns/${campaignId}?experimentId=${experimentId}`;

/**
 * JOURNEYS
 */
export const getJourneyStudioUrl = (id: string | number, workflowType?: WorkflowType) =>
  `/workflows/${id}/edit?workflowType=${workflowType}`;

export const getJourneyEditorLinkWithExperimentTakeover = (
  workflowId: number,
  workflowStatus: WorkflowType,
  nodeId: number,
  experimentId: number,
) =>
  `/${workflowId}/edit?mode=beta&workflowType=${workflowStatus}&nodeId=${nodeId}&experimentId=${experimentId}`;

/**
 * SEGMENTATION
 */
export const getListUrl = (id: string | number) => `/segmentation?emailListId=${id}`;

/**
 * TEMPLATES
 */
export const getTemplateEditorUrl = (
  id: string | number,
  templateLocales?: TemplateLocales | null,
  featureFlags?: Record<string, boolean>,
) => {
  const { isLocaleImprovementsEnabled = true } = featureFlags || {};
  return isLocaleImprovementsEnabled
    ? `/templates/edit?templateId=${id}${
        templateLocales?.templateDefaultLocale
          ? `&locale=${templateLocales?.templateDefaultLocale?.tag}`
          : ''
      }`
    : `/templates/edit?templateId=${id}`;
};
